import { FC, useEffect, useState } from "react";
import { take } from "ramda";
import { Text, Heading, Stack, Grid } from "@chakra-ui/react";
import { captureException } from "@sentry/nextjs";

import Button from "components/Chakra/Button";
import { useAlertModal } from "modals/useAlertModal";
import { CreateUserApiResponse } from "@ud/api";
import { ICreditCheckPerson, IUser } from "utils/user";
import GetCreditCheckListApiEndpoint from "utils/creditCheck/api/GetCreditCheckList";
import ContactBadge from "components/ContactBadge";
import Container from "components/Chakra/Container";
import MetaHead from "components/MetaHead";
import { useTracking } from "contexts/Tracking";
import { ESessionItem, getSessionItemAndRemove } from "helpers/sessions";
import Registers from "./components/Registers";
import Statistics from "./components/Statistics";
import PersonList from "./components/PersonList";
import CheckPreview from "./components/CheckPreview";
import PersonForm from "./components/PersonForm";
import CustomSpinner from "components/CustomSpinner";
import useApiEndpoint from "hooks/useApiEndpoint";
import useGlobalState from "hooks/useGlobalState";
import useUserCRM from "hooks/useUserCRM";
import useRouter from "hooks/useRouter";

type CreditCheckProps = {
  isSelfCheck?: boolean;
  payment?: "success" | "error" | "usedError";
  newPayment?: "success" | "error" | "usedError";
  promo?: string;
};

const CreditCheck: FC<CreditCheckProps> = ({
  isSelfCheck = true,
  payment,
  promo,
  newPayment,
}) => {
  const { user } = useGlobalState();
  const { trackEvent } = useTracking();

  const [data, setData] = useState<Array<ICreditCheckPerson>>([]);
  const [isLoading, setLoading] = useState(Boolean(user));
  const [showMaxReports, setMaxReports] = useState(3);

  const { getUserByEmail, updateUser, createUser, createEvent } = useUserCRM();

  const { openModal: openAlertModal } = useAlertModal();

  const { callEndpoint: getCreditCheckListApi } = useApiEndpoint(
    GetCreditCheckListApiEndpoint,
  );

  useEffect(() => {
    sessionStorage.setItem(
      ESessionItem.IS_SELF_CHECK.toString(),
      isSelfCheck.toString(),
    );
    if (
      (payment && payment === "success") ||
      (newPayment && newPayment === "success")
    ) {
      trackEvent({
        "dl.eCat": "CC",
        "dl.eAct": "purchase",
        "dl.eLab": "{{sebeprovereni/provereni}}",
        "dl.price": "{{price}}",
        "GA ecomerce": "",
      });
      openAlertModal({
        animation: "check",
        title: "Úspěch",
        children: "Zakoupení prověření proběhlo v pořádku.",
      });
    } else if (
      (payment && payment === "error") ||
      (newPayment && newPayment === "error")
    ) {
      openAlertModal({
        animation: "error",
        title: "Bohužel",
        children: "Zakoupení prověření neproběhlo v pořádku. Zkuste to znovu.",
      });
    } else if (payment && payment === "usedError") {
      openAlertModal({
        animation: "error",
        title: "Vaše jedno prověření zdarma jste již využili",
        children: (
          <>
            <Text mb={3}>
              Pro objednání balíčku více prověření za <b>zvýhodněnou cenu</b>{" "}
              kontaktujte, prosím, našeho obchodního zástupce.
            </Text>
            <ContactBadge
              imageUrl="/img/common/kolacek.jpg"
              name="Petr Koláček"
              title="Key Account Manager"
              email="kolacek@ulovdomov.cz"
            />
          </>
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }
    if (user && newPayment === "success") {
      const callApiPaymentSuccess = async (passUser: IUser) => {
        try {
          const userFromCRM = await getUserByEmail(passUser.email);
          if (userFromCRM) {
            await updateUser(userFromCRM.id, {
              premiumExpirationDate: passUser.premiumTill || undefined,
              selfCheckExpirationDate: passUser.selfCheckTill || undefined,
            });
            createEvent({
              userId: userFromCRM.id,
              eventName: "cc_purchased",
            });
          } else {
            const userRes = await createUser({
              email: passUser.email,
              userId: passUser.id,
              premiumExpirationDate: passUser.premiumTill || undefined,
              selfCheckExpirationDate: passUser.selfCheckTill || undefined,
              consents: [],
              offers: [],
            });
            const userData = userRes as unknown as CreateUserApiResponse;
            createEvent({
              userId: userData.id,
              eventName: "cc_purchased",
            });
          }
        } catch (err) {
          captureException(err);
        }
      };

      callApiPaymentSuccess(user);
    }
    setLoading(true);

    const callCreditCheckListApi = async () => {
      try {
        const creditCheckRes = await getCreditCheckListApi();

        if (!creditCheckRes.data) {
          return;
        }

        setData(creditCheckRes.data);
        setLoading(false);
      } catch (err) {
        captureException(err);
      }
    };

    callCreditCheckListApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  let results = <CheckPreview />;
  if (isLoading) {
    results = (
      <CustomSpinner
        alignContent="center"
        w="100%"
        h="100%"
        justifyContent="center"
      />
    );
  } else if (data.length) {
    results = (
      <>
        <PersonList persons={take(showMaxReports, data)} />
        {data.length > showMaxReports && (
          <Stack alignItems="center" mt="30px">
            <Button
              trackEventPayload={{
                "dl.eCat": "CC",
                "dl.eAct": "click_zobrazit_nahled",
                "dl.eLab": isSelfCheck ? "sebeprovereni" : "provereni",
              }}
              onClick={() => setMaxReports((p) => p + 3)}
              variant="link"
            >
              Zobrazit více
            </Button>
          </Stack>
        )}
      </>
    );
  }

  return (
    <>
      <MetaHead
        title="Prověřte rizikovost kohokoliv – rychle a bez práce"
        description="Prověříme kohokoliv v 6 nejdůležitějších rejstřících. Vy tak máte jistotu, že víte s kým vstupujete do smluvního vztahu."
      />
      <Container>
        <Heading as="h1" mt="5rem">
          {!isSelfCheck
            ? "Prověření bezdlužnosti"
            : "Vynikněte mezi zájemci díky sebeprověření"}
        </Heading>

        <Stack spacing={0} alignItems="center" mb="90px" mt="30px">
          <Text textAlign="center" size="large" as="h2">
            Zvyšte svou důvěryhodnost v očích majitelů.
          </Text>
        </Stack>

        <Grid
          mb="90px"
          gap="30px"
          templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }}
        >
          <PersonForm isSelfCheck={isSelfCheck} promo={promo} />
          {results}
        </Grid>

        <Statistics isSelfCheck={isSelfCheck} />
        <Registers />

        <Stack mb="60px" />
      </Container>
    </>
  );
};

const getPaymentStatus = (): CreditCheckProps["payment"] => {
  const url = typeof window === "undefined" ? "" : window.location.href;

  if (url.includes("uspesne")) {
    return "success";
  }

  if (url.includes("/platba-selhala")) {
    return "error";
  }

  if (url.includes("jiz-pouzito")) {
    return "usedError";
  }

  return undefined;
};

const getNewPaymentStatus = (query: any) => {
  if (query.platba === "zaplacena") {
    return "success";
  }

  if (query.platba === "nezaplacena") {
    return "error";
  }

  return undefined;
};

export const CreditCheckRoute = () => {
  const { query } = useRouter();
  const { trackEvent } = useTracking();

  const paymentStatus = getPaymentStatus();
  const newPaymentStatus = getNewPaymentStatus(query);

  useEffect(() => {
    if (paymentStatus === "success") {
      const price = getSessionItemAndRemove(ESessionItem.PRICE.toString());
      const isSelfCheck = Boolean(
        getSessionItemAndRemove(ESessionItem.IS_SELF_CHECK.toString()),
      );

      trackEvent({
        "dl.eCat": "CC",
        "dl.eAct": "purchase",
        "dl.eLab": isSelfCheck ? "sebeprovereni" : "provereni",
        "dl.price": price,
        "GA ecomerce": "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus]);

  return (
    <CreditCheck
      isSelfCheck={false}
      payment={paymentStatus}
      newPayment={newPaymentStatus}
      promo={query.promo?.toString() || ""}
    />
  );
};

const CreditCheckSelf = () => {
  const { query } = useRouter();

  return (
    <CreditCheck
      payment={getPaymentStatus()}
      newPayment={getNewPaymentStatus(query)}
    />
  );
};

export default CreditCheckSelf;
